import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { X, Search, ChevronLeft, Sparkles } from "lucide-react";
import api from "../../api";
import ToolkitPreview, { TYPE_LABEL, TYPE_STYLE } from "./ToolkitPreview";

// Pick a toolkit item and give it to a patient. Without `patientId` the
// clinician searches for the patient first (Toolkit page). `suggestions`
// (from the note text) show first. `initialCode` opens straight on an item.
export default function AssignToolModal({ patientId, patientName, consultationId, appointmentId, suggestions = [], initialCode, onClose, onAssigned }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [due, setDue] = useState("");
  const [saving, setSaving] = useState(false);
  const [patient, setPatient] = useState(patientId ? { id: patientId, name: patientName } : null);
  const [patientQ, setPatientQ] = useState("");
  const [patientResults, setPatientResults] = useState([]);

  useEffect(() => { api.getToolkit({ audience: "patient" }).then((r) => setItems(r.data || [])).catch(() => setItems([])); }, []);
  useEffect(() => { if (initialCode) open(initialCode); }, [initialCode]); // eslint-disable-line
  useEffect(() => {
    if (patientId || patientQ.trim().length < 2) { setPatientResults([]); return; }
    const t = setTimeout(() => api.fetchPatients({ search: patientQ.trim(), limit: 8 })
      .then((r) => setPatientResults((r.data?.data || []).filter((p) => p.patienttype !== "Virtual")))
      .catch(() => setPatientResults([])), 300);
    return () => clearTimeout(t);
  }, [patientQ, patientId]);

  const open = (code) => api.getToolkitItem(code).then((r) => setSelected(r.data)).catch(() => toast.error("Couldn't open that item."));
  const categories = [...new Set(items.map((i) => i.category).filter(Boolean))].sort();
  const shown = items.filter((i) => (!category || i.category === category)
    && (!q.trim() || `${i.title} ${i.description} ${(i.tags || []).join(" ")}`.toLowerCase().includes(q.trim().toLowerCase())));

  const assign = async () => {
    if (!patient) { toast.error("Choose the patient first."); return; }
    setSaving(true);
    try {
      const r = await api.assignHomework(patient.id, { item_code: selected.code, note, due_date: due || undefined, consultation_id: consultationId || undefined, appointment_id: appointmentId || undefined });
      toast.success(r?.data?.patient_visible === false
        ? `${selected.title} assigned - ${patient.name} gets it in My Care once you sign off the session note`
        : `${selected.title} shared with ${patient.name} - they'll find it in My Care`);
      if (r.data?.language_note) toast.info(r.data.language_note);
      onAssigned && onAssigned(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't share it.");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-3 sm:p-4" onClick={onClose}>
      <div className="flex w-full max-w-2xl max-h-[92vh] flex-col rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 border-b border-secondary-100 px-5 py-3">
          {selected && <button type="button" onClick={() => setSelected(null)} aria-label="Back to the list" className="p-1 rounded-full hover:bg-secondary-100"><ChevronLeft className="w-5 h-5" /></button>}
          <h3 className="flex-1 font-bold text-secondary-900">{selected ? selected.title : `Assign homework${patient?.name ? ` to ${patient.name}` : ""}`}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded-full text-secondary-400 hover:text-secondary-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!consultationId && !appointmentId && patient && (
            <p className="rounded-lg bg-secondary-50 px-3 py-2 text-xs text-secondary-600">
              Given outside a session. It will show under "To review today" on the patient's next session note.
            </p>
          )}
          {!selected ? (
            <>
              {suggestions.length > 0 && (
                <div>
                  <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-secondary-500 mb-2"><Sparkles className="w-4 h-4 text-primary-500" /> Suggested from this session's note</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.map((s) => (
                      <button key={s.code} type="button" onClick={() => open(s.code)} title={s.matched?.length ? `Matched: ${s.matched.join(", ")}` : ""}
                        className="rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-800 hover:bg-primary-100">{s.title}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-secondary-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search: sleep, worry, breathing…" aria-label="Search the toolkit"
                    className="w-full rounded-lg border border-secondary-300 py-2 pl-8 pr-3 text-sm" />
                </div>
                <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category" className="rounded-lg border border-secondary-300 px-2 py-2 text-sm bg-white">
                  <option value="">All topics</option>
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <ul className="grid gap-2 sm:grid-cols-2">
                {shown.map((i) => (
                  <li key={i.code}>
                    <button type="button" onClick={() => open(i.code)} className="h-full w-full rounded-xl border border-secondary-200 p-3 text-left hover:border-primary-300 hover:bg-primary-50/40">
                      <span className="flex items-center gap-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${TYPE_STYLE[i.type] || ""}`}>{TYPE_LABEL[i.type] || i.type}</span>
                        {i.minutes && <span className="text-[11px] text-secondary-500">{i.minutes} min</span>}
                      </span>
                      <span className="mt-1 block text-sm font-semibold text-secondary-900">{i.title}</span>
                      <span className="block text-xs text-secondary-600 line-clamp-2">{i.description}</span>
                    </button>
                  </li>
                ))}
                {shown.length === 0 && <li className="text-sm text-secondary-500">Nothing matches.</li>}
              </ul>
            </>
          ) : (
            <>
              <ToolkitPreview item={selected} />
              <p className="rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 text-xs text-sky-900">
                The patient gets a notification and finds this in My Care on their phone. They fill it in after the session, in their own time - nothing needs doing during the call. Their answers appear in the Homework tab.
              </p>
              <div className="rounded-xl border border-secondary-200 bg-secondary-50 p-3 space-y-3">
                {!patientId && (
                  <div>
                    <p className="text-xs font-semibold text-secondary-700 mb-1">Patient</p>
                    {patient ? (
                      <p className="flex items-center gap-2 text-sm"><strong>{patient.name}</strong>
                        <button type="button" onClick={() => setPatient(null)} className="text-xs text-primary-700 underline">Change</button></p>
                    ) : (
                      <>
                        <input value={patientQ} onChange={(e) => setPatientQ(e.target.value)} placeholder="Search by name or phone" aria-label="Search patients"
                          className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm" />
                        {patientResults.length > 0 && (
                          <ul className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-secondary-200 bg-white">
                            {patientResults.map((p) => (
                              <li key={p.id}><button type="button" onClick={() => setPatient(p)} className="w-full px-3 py-2 text-left text-sm hover:bg-secondary-50">{p.name} <span className="text-xs text-secondary-500">{p.contact}</span></button></li>
                            ))}
                          </ul>
                        )}
                      </>
                    )}
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-secondary-700 mb-1">Message to the patient (optional)</p>
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={1000} placeholder="e.g. Try one each time you notice your mood drop. We'll go through it next session."
                    className="w-full rounded-lg border border-secondary-300 bg-white p-2 text-sm" />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-secondary-700">Try to finish by (optional)</span>
                  <input type="date" value={due} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDue(e.target.value)} aria-label="Due date"
                    className="rounded-lg border border-secondary-300 bg-white px-2 py-1.5 text-sm" />
                </div>
              </div>
            </>
          )}
        </div>
        {selected && (
          <div className="flex justify-end gap-2 border-t border-secondary-100 px-5 py-3">
            <button type="button" onClick={() => setSelected(null)} className="rounded-lg border border-secondary-300 px-4 py-2 text-sm">Back</button>
            <button type="button" onClick={assign} disabled={saving || !patient} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {saving ? "Sharing…" : `Share with ${patient?.name || "patient"}`}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
